import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import styles from './ai-prompt-box-submit-button.module.scss';
import actionButtonStyles from '../../ai-prompt-box-action-button.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {ClickableDiv} from '@Components/clickable-div';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {AsyncVoidFunction} from '@Utils/general.util';
import {hasActiveAttachmentLoaders, isPromptBoxProcessing} from '@Components/ai-prompt-box/ai-prompt-box-library';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {isTouchDevice} from '@Utils/browser.util';

interface SubmitButtonProps {
  id: string;
  onSubmit: AsyncVoidFunction;
  onStop?: AsyncVoidFunction;
  onRegenerate?: AsyncVoidFunction;
}

export function AiPromptBoxSubmitButton(props: SubmitButtonProps): ReactElement {
  const {state, actions} = useAiPromptBox(props.id);
  const dailyLimitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isLoading = isPromptBoxProcessing(state.promptBoxState);
  const isAttachmentLoading = hasActiveAttachmentLoaders(state.attachmentLoaders);
  const isStoppable = state.promptBoxState === PromptBoxState.STOPPABLE_LOADING;
  const isRegenerate = state.promptBoxState === PromptBoxState.REGENERATE;
  const isDailyLimitReached = state.isDailyLimitReached ?? false;

  const hasValidPrompt = (): boolean => {
    return !!state.prompt && !!state.prompt.replace(/ /g, '').length;
  };

  const isPromptBoxActive = (): boolean => {
    return state.promptBoxState === PromptBoxState.ACTIVE;
  };

  const canSubmit = (): boolean => {
    return (hasValidPrompt() && isPromptBoxActive()) || isRegenerate;
  };

  const isActive = !isLoading && !isAttachmentLoading && canSubmit();

  const sizeClassMap = {
    [AiPromptBoxSize.SMALL]: actionButtonStyles.actionButtonSmall,
    [AiPromptBoxSize.MEDIUM]: actionButtonStyles.actionButtonMedium,
    [AiPromptBoxSize.SINGLE_LINE]: actionButtonStyles.actionButtonSingleLine,
    [AiPromptBoxSize.SMALL_EXTENDED]: actionButtonStyles.actionButtonSmall,
  };

  const sizeIconSizeMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_20,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_16,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_16,
  };

  const loaderSizeMap = {
    [AiPromptBoxSize.SMALL]: LOADER_SIZE.SMALL,
    [AiPromptBoxSize.MEDIUM]: LOADER_SIZE.DEFAULT,
    [AiPromptBoxSize.SINGLE_LINE]: LOADER_SIZE.SMALL,
    [AiPromptBoxSize.SMALL_EXTENDED]: LOADER_SIZE.SMALL,
  };

  const stopIconSizeMap = {
    [AiPromptBoxSize.SMALL]: IconSize.SIZE_ICON_8,
    [AiPromptBoxSize.MEDIUM]: IconSize.SIZE_ICON_12,
    [AiPromptBoxSize.SINGLE_LINE]: IconSize.SIZE_ICON_8,
    [AiPromptBoxSize.SMALL_EXTENDED]: IconSize.SIZE_ICON_8,
  };

  const getRegenerateButton = (): ReactElement => {
    return (
      <ClickableDiv
        className={`radius-round flex-center bg-ai-dark content-body-white _cursor-pointer ${styles.submitButtonContainer} ${sizeClassMap[state.size]}`}
        onClick={handleClick}
      >
        <Icon icon={'icon-rotate-ccw'} size={sizeIconSizeMap[state.size]} type={IconType.SECONDARY} />
      </ClickableDiv>
    );
  };

  const getLoader = (): ReactElement => {
    return (
      <ClickableDiv
        className={`radius-round flex-center bg-ai-dark content-body-white _cursor-pointer ${styles.submitButtonContainer} ${sizeClassMap[state.size]}`}
        onClick={handleClick}
      >
        <CircularProgressLoader color={LOADER_COLOR.WHITE} size={loaderSizeMap[state.size]} classes="_unmargin" />
      </ClickableDiv>
    );
  };

  const getLoaderWithStopIcon = (): ReactElement => {
    return (
      <ClickableDiv
        className={`radius-round flex-center bg-ai-dark content-body-white _cursor-pointer ${styles.submitButtonContainer} ${sizeClassMap[state.size]}`}
        onClick={handleClick}
      >
        <CircularProgressLoader color={LOADER_COLOR.WHITE} size={loaderSizeMap[state.size]} classes={styles.stopLoader} />
        <Icon icon={'icon-square-rounded'} size={stopIconSizeMap[state.size]} type={IconType.SECONDARY} className={styles.stopIconOverlay} />
      </ClickableDiv>
    );
  };

  const getButton = (): ReactElement => {
    const button = (
      <ClickableDiv
        className={`radius-round flex-center ${styles.submitButtonContainer} ${sizeClassMap[state.size]} ${!isActive || isDailyLimitReached ? styles.submitButtonDisabled : 'bg-ai-dark'}`}
        onClick={handleClick}
      >
        <Icon icon={'icon-chat-send'} size={sizeIconSizeMap[state.size]} type={IconType.SECONDARY} isDisabled={!isActive || isDailyLimitReached} />
      </ClickableDiv>
    );

    if (isDailyLimitReached) {
      if (isTouchDevice()) {
        return button;
      }

      return (
        <OverlayTrigger
          trigger={['hover', 'focus']}
          show={!state.showDailyLimitError ? undefined : false}
          overlay={<Tooltip className="spacing-m-b-1 radius-8 _rounded-arrow">{window.i18next.t('pmwjs_daily_limit_reached')}</Tooltip>}
          placement={'top'}
        >
          {button}
        </OverlayTrigger>
      );
    }

    return button;
  };

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();

    if (isDailyLimitReached) {
      actions.update({showDailyLimitError: true});

      if (dailyLimitTimeoutRef.current) {
        clearTimeout(dailyLimitTimeoutRef.current);
      }

      dailyLimitTimeoutRef.current = setTimeout(() => {
        actions.update({showDailyLimitError: false});
      }, 3000);
      return;
    }

    if (isStoppable) {
      void props.onStop?.();
      return;
    }

    if (isAttachmentLoading) {
      return;
    }

    if (isRegenerate) {
      void props.onRegenerate?.();
      return;
    }

    if (isLoading) {
      return;
    }

    if (isActive) {
      void props.onSubmit();
    }
  };

  const getSubmitButton = (): ReactElement => {
    if (isStoppable) {
      return getLoaderWithStopIcon();
    }

    if (isRegenerate) {
      return getRegenerateButton();
    }

    if (isLoading) {
      return getLoader();
    }

    return getButton();
  };

  return getSubmitButton();
}
